import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Phone, Mail, MessageSquare, Users, FileText, MoreHorizontal, PhoneCall, Info } from 'lucide-react';
import { useActivityStore } from '../../store/activityStore';
import { PersonPicker } from '../shared/PersonPicker';
import type { ActivityType, Temperature, MeetingLocation } from '../../types';
import toast from 'react-hot-toast';
import { TEMPERATURE_STYLES, TEMPERATURE_ORDER } from '../shared/temperatureConfig';
import { shouldNudgeForMissingLocation } from '../../lib/locationNudge';
import { formatPhone } from '../../lib/formatPhone';
import type { CallableNumber } from '../../lib/personPrecedence';

interface AddActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  personId?: string;
  personName?: string;
  /** Distinct callable numbers (preferred first); each renders a click-to-dial-and-log button. */
  phoneNumbers?: CallableNumber[];
  currentTemperature?: Temperature | null;
  organizationName?: string | null;
  onActivityCreated?: (updatedTemperature: Temperature | null, updatedLastActivityAt: string | null) => void;
  prefill?: {
    type?: ActivityType;
    summary?: string;
    completingActivityId?: string; // When set, we're completing a scheduled activity
  };
  assignmentNudge?: {
    variant: 'assigned-to-other' | 'unassigned';
    assigneeName?: string;
  } | null;
}

// Activity types with icons
const ACTIVITY_TYPES: { type: ActivityType; label: string; icon: typeof Phone }[] = [
  { type: 'CALL', label: 'Call', icon: Phone },
  { type: 'EMAIL', label: 'Email', icon: Mail },
  { type: 'TEXT', label: 'Text', icon: MessageSquare },
  { type: 'IN_PERSON', label: 'In Person', icon: Users },
  { type: 'NOTE', label: 'Note', icon: FileText },
  { type: 'OTHER', label: 'Other', icon: MoreHorizontal },
];

// Meeting location options (shown when rep says "No" to on-campus, or no org linked)
const MEETING_LOCATION_OPTIONS: { value: MeetingLocation; label: string }[] = [
  { value: 'CONFERENCE', label: 'Conference' },
  { value: 'RESTAURANT', label: 'Restaurant' },
  { value: 'GAME_TOURNAMENT', label: 'Game / Tournament' },
  { value: 'OTHER', label: 'Other' },
];

// Helper to format current datetime for datetime-local input
const getCurrentDateTime = (): string => {
  const now = new Date();
  const offset = now.getTimezoneOffset();
  const localDate = new Date(now.getTime() - offset * 60 * 1000);
  return localDate.toISOString().slice(0, 16);
};

// Convert a zone-naive datetime-local value (e.g. "2026-06-02T14:30") into a UTC
// ISO string carrying the browser's offset, so the server stores the correct
// instant instead of reinterpreting the wall-clock in its own timezone (CRM-30).
const toIsoFromLocalInput = (localValue: string): string =>
  new Date(localValue).toISOString();

/**
 * Modal for logging a new activity against a person
 */
export const AddActivityModal = ({
  isOpen,
  onClose,
  personId,
  personName,
  phoneNumbers,
  currentTemperature,
  organizationName,
  onActivityCreated,
  prefill,
  assignmentNudge,
}: AddActivityModalProps) => {
  const { createActivity, completeActivity, isSaving, saveError, clearSaveError } = useActivityStore();
  const navigate = useNavigate();

  const isCompleting = !!prefill?.completingActivityId;

  // Person picker state (used when personId is not provided)
  const [selectedPerson, setSelectedPerson] = useState<{ id: string; firstName: string; lastName: string } | null>(null);
  const effectivePersonId = personId || selectedPerson?.id;
  const effectivePersonName = personName || (selectedPerson ? `${selectedPerson.firstName} ${selectedPerson.lastName}` : '');

  // Form state
  const [activityType, setActivityType] = useState<ActivityType>('CALL');
  const [summary, setSummary] = useState('');
  const [activityAt, setActivityAt] = useState('');
  const [temperature, setTemperature] = useState<Temperature | null>(null);
  const [meetingLocation, setMeetingLocation] = useState<MeetingLocation | null>(null);
  const [showOffCampusOptions, setShowOffCampusOptions] = useState(false);

  const summaryRef = useRef<HTMLTextAreaElement>(null);

  // Reset form when modal opens, applying prefill if provided
  useEffect(() => {
    if (isOpen) {
      setActivityType(prefill?.type ?? 'CALL');
      setSummary(prefill?.summary ?? '');
      setActivityAt(prefill?.completingActivityId ? getCurrentDateTime() : '');
      setTemperature(null);
      setMeetingLocation(null);
      setShowOffCampusOptions(false);
      setSelectedPerson(null);
      clearSaveError();
      // Focus summary field after a brief delay
      setTimeout(() => {
        summaryRef.current?.focus();
      }, 100);
    }
    // Key the reset on the OPEN TRANSITION only. `prefill` is an inline object
    // literal at both call sites (new identity every parent render) and
    // clearSaveError() writes the store those parents subscribe to — listing
    // `prefill` as a dep closes an infinite effect → store write → parent
    // re-render loop (React #185: the white-page-on-Complete bug).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Clear meeting location when switching away from IN_PERSON
  useEffect(() => {
    if (activityType !== 'IN_PERSON') {
      setMeetingLocation(null);
      setShowOffCampusOptions(false);
    }
  }, [activityType]);

  // Handle click-to-dial. `value` is the RAW stored number (never formatted) so the dialer gets real digits.
  const handleDialAndLog = (value: string) => {
    if (!value) return;

    // Open phone dialer
    window.location.href = `tel:${value}`;

    // Set activity type to CALL
    setActivityType('CALL');

    // Set date/time to now
    setActivityAt(getCurrentDateTime());

    // Focus the summary field after a brief delay (allows dialer to open)
    setTimeout(() => {
      summaryRef.current?.focus();
    }, 100);
  };

  // Handle temperature button click (toggle on/off)
  const handleTemperatureClick = (temp: Temperature) => {
    setTemperature((prev) => (prev === temp ? null : temp));
  };

  // Handle "Yes" to on-campus prompt
  const handleOnCampus = () => {
    setMeetingLocation('ON_CAMPUS');
    setShowOffCampusOptions(false);
  };

  // Handle "No" to on-campus prompt — show off-campus options
  const handleNotOnCampus = () => {
    setMeetingLocation(null);
    setShowOffCampusOptions(true);
  };

  // Handle off-campus option selection (collapses options, shows selected badge)
  const handleOffCampusSelect = (location: MeetingLocation) => {
    setMeetingLocation(location);
    setShowOffCampusOptions(false);
  };

  // Silently request browser geolocation with a timeout
  const getGeolocation = (): Promise<{ latitude: number; longitude: number; locationAccuracy: number } | null> => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            locationAccuracy: position.coords.accuracy,
          });
        },
        () => {
          // Permission denied, timeout, or unavailable — silently resolve null
          resolve(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 30000,
        }
      );
    });
  };

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!effectivePersonId) {
      toast.error('Please select a person');
      return;
    }

    if (!summary.trim()) {
      toast.error('Please enter a summary');
      return;
    }

    // Validate meeting location BEFORE geo capture (avoid 10s wait for validation error)
    if (activityType === 'IN_PERSON' && !meetingLocation) {
      toast.error('Please select where you\'re meeting');
      return;
    }

    try {
      // Silently capture geolocation (up to 10s timeout, null if unavailable)
      const location = await getGeolocation();

      if (isCompleting && prefill?.completingActivityId) {
        // Complete a scheduled activity
        const result = await completeActivity(effectivePersonId, prefill.completingActivityId, {
          type: activityType,
          summary: summary.trim(),
          activityAt: activityAt ? toIsoFromLocalInput(activityAt) : undefined,
          // Untouched selector = "no change" — sending null would be an explicit
          // clear (the API deliberately treats null as clear; CRM-111).
          ...(temperature !== null && { temperature }),
          ...(meetingLocation && { meetingLocation }),
          ...(location && {
            latitude: location.latitude,
            longitude: location.longitude,
            locationAccuracy: location.locationAccuracy,
          }),
        });

        toast.success('Activity completed');

        // Notify parent of updated person data
        const person = result.person as { temperature: Temperature | null; lastActivityAt: string | null } | undefined;
        onActivityCreated?.(person?.temperature ?? null, person?.lastActivityAt ?? null);
      } else {
        // Normal create flow
        const result = await createActivity(effectivePersonId, {
          type: activityType,
          summary: summary.trim(),
          activityAt: activityAt ? toIsoFromLocalInput(activityAt) : undefined,
          ...(temperature !== null && { temperature }),
          ...(meetingLocation && { meetingLocation }),
          ...(location && {
            latitude: location.latitude,
            longitude: location.longitude,
            locationAccuracy: location.locationAccuracy,
          }),
        });

        toast.success('Activity logged');

        // Notify parent of updated person data
        onActivityCreated?.(result.updatedTemperature, result.updatedLastActivityAt);
      }

      if (shouldNudgeForMissingLocation(activityType, location)) {
        toast(
          (t) => (
            <span
              className="cursor-pointer"
              onClick={() => {
                toast.dismiss(t.id);
                navigate('/settings');
              }}
            >
              Couldn't capture your location. Tap to fix in Settings →
            </span>
          ),
          { icon: '📍', duration: 6000 },
        );
      }

      onClose();
    } catch (error) {
      // Error already shown via store
      console.error('Failed to log activity:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {isCompleting ? 'Complete Activity' : 'Log Activity'}
              </h2>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-5">
              {/* Person picker (when no personId provided) or static person name */}
              {!personId ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Person <span className="text-red-500">*</span>
                  </label>
                  <PersonPicker
                    value={selectedPerson}
                    onChange={setSelectedPerson}
                    placeholder="Search for a person..."
                    required
                  />
                </div>
              ) : (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  For: <span className="font-medium text-gray-900 dark:text-gray-100">{effectivePersonName}</span>
                </p>
              )}

              {/* Soft assignment nudge — informational only, no action */}
              {assignmentNudge && (
                <div className="flex items-start gap-2 rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-800 dark:border-blue-900/40 dark:bg-blue-900/20 dark:text-blue-200">
                  <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                  <span>
                    {assignmentNudge.variant === 'assigned-to-other'
                      ? `This contact is assigned to ${assignmentNudge.assigneeName ?? 'another rep'}.`
                      : 'This contact has no assigned rep.'}
                  </span>
                </div>
              )}

              {/* Error banner */}
              {saveError && (
                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-3 text-red-800 dark:text-red-300 text-sm">
                  {saveError}
                </div>
              )}

              {/* Click-to-Call Button(s) — one per distinct number; tags shown only when two differ */}
              {phoneNumbers && phoneNumbers.length > 0 && (
                <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 space-y-2">
                  {phoneNumbers.map((n) => (
                    <button
                      key={n.value}
                      type="button"
                      onClick={() => handleDialAndLog(n.value)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium transition-colors"
                    >
                      <PhoneCall className="w-5 h-5 flex-shrink-0" />
                      Call {formatPhone(n.value)}
                      {n.label === 'Preferred' && (
                        <span className="inline-flex items-center text-[9px] font-bold uppercase tracking-wide bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded">
                          Preferred
                        </span>
                      )}
                      {n.label === 'Blast' && (
                        <span className="inline-flex items-center text-[9px] font-bold uppercase tracking-wide bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 px-1.5 py-0.5 rounded">
                          Blast
                        </span>
                      )}
                    </button>
                  ))}
                  <p className="text-xs text-green-700 dark:text-green-400 text-center">
                    Opens dialer and sets time to now
                  </p>
                </div>
              )}

              {/* Activity Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Activity Type
                </label>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  {ACTIVITY_TYPES.map(({ type, label, icon: Icon }) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setActivityType(type)}
                      className={`flex flex-col items-center justify-center px-2 py-3 rounded-lg border-2 transition-all ${
                        activityType === type
                          ? 'border-blast-600 bg-blast-50 text-blast-700 dark:border-blast-400 dark:bg-blast-950 dark:text-blast-400'
                          : 'border-gray-200 hover:border-gray-300 text-gray-600 dark:border-gray-600 dark:hover:border-gray-500 dark:text-gray-400'
                      }`}
                    >
                      <Icon className="w-5 h-5 mb-1" />
                      <span className="text-xs font-medium">{label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Meeting Location (IN_PERSON only) */}
              {activityType === 'IN_PERSON' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Meeting Location <span className="text-red-500">*</span>
                  </label>

                  {/* On-campus prompt (only when org is linked) */}
                  {organizationName && !showOffCampusOptions && !meetingLocation && (
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        Are you at <span className="font-medium text-gray-900 dark:text-gray-100">{organizationName}</span>?
                      </p>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={handleOnCampus}
                          className="px-4 py-2 rounded-lg border-2 border-gray-200 hover:border-green-500 hover:bg-green-50 text-sm font-medium text-gray-700 dark:border-gray-600 dark:hover:border-green-400 dark:hover:bg-green-950 dark:text-gray-300 transition-all"
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          onClick={handleNotOnCampus}
                          className="px-4 py-2 rounded-lg border-2 border-gray-200 hover:border-gray-400 text-sm font-medium text-gray-700 dark:border-gray-600 dark:hover:border-gray-400 dark:text-gray-300 transition-all"
                        >
                          No
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Selected ON_CAMPUS state */}
                  {meetingLocation === 'ON_CAMPUS' && (
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center px-3 py-1.5 rounded-lg bg-green-50 border-2 border-green-500 text-sm font-medium text-green-700 dark:bg-green-950 dark:border-green-400 dark:text-green-400">
                        On Campus{organizationName ? ` — ${organizationName}` : ''}
                      </span>
                      <button
                        type="button"
                        onClick={() => { setMeetingLocation(null); setShowOffCampusOptions(false); }}
                        className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        Change
                      </button>
                    </div>
                  )}

                  {/* Off-campus options (shown after "No" or when no org linked) */}
                  {(showOffCampusOptions || !organizationName) && meetingLocation !== 'ON_CAMPUS' && (
                    <div>
                      {!organizationName && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                          Where are you meeting?
                        </p>
                      )}
                      <div className="flex flex-wrap gap-2">
                        {MEETING_LOCATION_OPTIONS.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => handleOffCampusSelect(opt.value)}
                            className={`px-3 py-2 rounded-lg border-2 transition-all text-sm font-medium ${
                              meetingLocation === opt.value
                                ? 'border-blast-600 bg-blast-50 text-blast-700 dark:border-blast-400 dark:bg-blast-950 dark:text-blast-400'
                                : 'border-gray-200 hover:border-gray-300 text-gray-700 dark:border-gray-600 dark:hover:border-gray-500 dark:text-gray-300'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Selected off-campus state (show selected with change option) */}
                  {meetingLocation && meetingLocation !== 'ON_CAMPUS' && !showOffCampusOptions && organizationName && (
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center px-3 py-1.5 rounded-lg bg-blast-50 border-2 border-blast-600 text-sm font-medium text-blast-700 dark:bg-blast-950 dark:border-blast-400 dark:text-blast-400">
                        {MEETING_LOCATION_OPTIONS.find(o => o.value === meetingLocation)?.label}
                      </span>
                      <button
                        type="button"
                        onClick={() => { setMeetingLocation(null); setShowOffCampusOptions(false); }}
                        className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        Change
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Date/Time */}
              <div>
                <label htmlFor="activityAt" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Date & Time <span className="text-gray-400 font-normal">(defaults to now)</span>
                </label>
                <input
                  type="datetime-local"
                  id="activityAt"
                  value={activityAt}
                  onChange={(e) => setActivityAt(e.target.value)}
                  className="input"
                />
              </div>

              {/* Temperature (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Update Temperature <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
                  {TEMPERATURE_ORDER.map((value) => {
                    const style = TEMPERATURE_STYLES[value];
                    const { Icon } = style;
                    const isSelected = temperature === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => handleTemperatureClick(value)}
                        className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border-2 transition-all text-sm ${
                          isSelected
                            ? `${style.selectedBorder} ${style.selectedBg} ${style.selectedText} font-medium`
                            : 'border-gray-200 hover:border-gray-300 text-gray-700 dark:border-gray-600 dark:hover:border-gray-500 dark:text-gray-300'
                        }`}
                      >
                        <Icon className={`h-4 w-4 ${isSelected ? style.iconClass : 'text-gray-400 dark:text-gray-500'}`} />
                        {style.label}
                      </button>
                    );
                  })}
                </div>
                {temperature && temperature !== currentTemperature && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                    Temperature will update from{' '}
                    <span className="font-medium">{currentTemperature || 'unset'}</span> to{' '}
                    <span className="font-medium">{temperature}</span>
                  </p>
                )}
              </div>

              {/* Summary */}
              <div>
                <label htmlFor="summary" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Summary <span className="text-red-500">*</span>
                </label>
                <textarea
                  ref={summaryRef}
                  id="summary"
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  rows={4}
                  placeholder="What happened? Key takeaways, next steps..."
                  className="input"
                  required
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  💡 Tip: Use your keyboard's microphone button to dictate
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={isSaving || !summary.trim() || !effectivePersonId}
              >
                {isSaving ? (
                  <>
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                    Saving...
                  </>
                ) : (
                  isCompleting ? 'Complete Activity' : 'Log Activity'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
